// ── episodeProgress ───────────────────────────────────────────────────────────
// The ONE place that decides what "mark the next episode watched" means. Every
// lean surface (My List rows, Tonight plans, Home Up Next, Calendar) used to
// hand-roll this advance, and they drifted (some passed a no-op seriesFinale
// flag, some fired a season-complete milestone, some didn't). Now they all call
// nextMark() and feed the result straight into wl.saveProgress, which owns the
// rest of the ceremony (wrap clapboard, finale completion, undo, in-sync).
//
// progress is the user's NEXT-to-watch episode. The returned `prog` is the new
// next-to-watch after this mark; `opts.watchedEp` is the episode just watched
// (always explicit so saveProgress never has to guess and mint a ghost episode).
import { getValidSeasons } from './episode.js'

export function nextMark(show, progress) {
  let season = progress?.season || 1
  let ep     = progress?.episode || 1

  // Real seasons only (specials/S0 dropped) so a season-0 boundary can't fire a
  // "Season 0 Complete" wrap and so a gap in specials doesn't confuse the lookup.
  const valid = getValidSeasons(show?.seasons)

  // Overshoot normalization: "caught up" on a finished season is deliberately
  // stored as {S, finalEp+1} — an episode that doesn't exist. When TMDB later
  // lists the next season, the raw pointer would mark that GHOST episode (the
  // clapboard read "Mark S1·E11 watched" on a 10-ep season, logged a phantom
  // activity, and the S2·E1 the user actually watched needed a second tap).
  // Roll the pointer forward to the next season's opener first.
  {
    const cur = valid.find(s => s.season_number === season)
    if (cur?.episode_count && ep > cur.episode_count) {
      const roll = valid.filter(s => s.season_number > season).sort((a, b) => a.season_number - b.season_number)[0]
      if (roll) { season = roll.season_number; ep = 1 }
    }
  }
  const watchedEp = { season, episode: ep }

  const seasonData = valid.find(s => s.season_number === season)
  // An unknown or zero episode_count (TMDB's season list lagging a season that just
  // started) means "no idea where this season ends", so advance within it; the old
  // `|| ep` fallback treated the current episode as the finale and rolled the pointer
  // into the next season after any mark.
  const epCount    = seasonData?.episode_count || Infinity

  // Last episode of the current season?
  if (ep >= epCount) {
    // Next EXISTING real season — not literally season+1 — so non-contiguous numbering
    // ([1,2,4]) advances into season 4 instead of parking past the season-2 finale.
    const nextSeason = valid.filter(s => s.season_number > season).sort((a, b) => a.season_number - b.season_number)[0]
    if (nextSeason) {
      // Cross into the next season. saveProgress fires the "That's a Wrap"
      // clapboard off this season boundary.
      return { prog: { season: nextSeason.season_number, episode: 1 }, opts: { watchedEp }, seasonComplete: season }
    }
    // No next-season data. Just advance the pointer past the finale. saveProgress
    // decides what that means: a confirmed-ended show gets the full completion
    // ceremony (its watchedEp == last aired); a returning show simply reads
    // "all caught up" until the next season airs. The old per-surface
    // `seriesFinale: true` flag was a no-op — saveProgress never read it.
    return { prog: { season, episode: ep + 1 }, opts: { watchedEp } }
  }

  // Plain incremental advance within the season.
  return { prog: { season, episode: ep + 1 }, opts: { watchedEp } }
}

// Compute the "catch up to the latest aired episode" jump. effLast is the
// effective last-aired marker (handles premiere-night null last_episode_to_air).
export function catchUpMark(effLast) {
  if (!effLast) return null
  return {
    prog: { season: effLast.season, episode: effLast.episode + 1 },
    opts: { watchedEp: { season: effLast.season, episode: effLast.episode } },
  }
}
